import type { ErrorResponse } from "./types.gen";

/**
 * Absolute base URL of the API.
 *
 * In production this is `https://api.berbench.com` — a different origin from
 * the app, on purpose, so the two tiers can be scaled and monitored
 * separately. In development it is `http://localhost:8080`, reached directly
 * rather than through a Vite proxy, so that CORS and the credentialed cookie
 * path are exercised on a laptop instead of first meeting them in production.
 */
export const API_BASE_URL: string =
  import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8080";

/**
 * An error carrying the API's structured body.
 *
 * The `code` is the field to branch on; `message` is for humans and may change.
 * `requestId` is what a user should quote in a support thread — it is the only
 * thing that ties their report to a server log line.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId?: string;

  constructor(status: number, body: Partial<ErrorResponse>) {
    super(body.message ?? `request failed with status ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.code = body.code ?? "unknown";
    this.requestId = body.request_id;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

/** Thrown when the API cannot be reached at all (offline, DNS, CORS). */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super("could not reach the BERBench API");
    this.name = "NetworkError";
    this.cause = cause;
  }
}

type RequestOptions = {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
};

/**
 * The single fetch wrapper every request in this app goes through.
 *
 * Two properties are load-bearing and must not be overridden per call site:
 *
 * - `credentials: "include"` — the session lives in a cookie set by
 *   api.berbench.com. Because that is a different origin, the browser will not
 *   attach it unless the request asks for it explicitly.
 * - `Content-Type: application/json` on every mutation — this is half of the
 *   CSRF defence. It makes the request non-simple, forcing a preflight the
 *   server rejects for any origin outside its allowlist. An HTML form cannot
 *   produce this content type, so a form cannot reach a mutating endpoint.
 *   Dropping it to "save a preflight" would quietly remove that protection.
 */
export async function apiFetch<T>(
  path: string,
  { method = "GET", body, signal }: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  const hasBody = body !== undefined;
  if (hasBody) {
    headers["Content-Type"] = "application/json";
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers,
      credentials: "include",
      body: hasBody ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (cause) {
    // fetch rejects only on transport-level failure. A CORS rejection also
    // lands here, with no detail — the browser withholds it from script by
    // design — which is why the API logs the rejection server-side.
    if (cause instanceof DOMException && cause.name === "AbortError") {
      throw cause;
    }
    throw new NetworkError(cause);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const payload = await readJson(response);

  if (!response.ok) {
    throw new ApiError(response.status, (payload ?? {}) as Partial<ErrorResponse>);
  }
  return payload as T;
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text === "") return undefined;
  try {
    return JSON.parse(text);
  } catch {
    // A non-JSON body from this API means something between us and it
    // answered instead — a proxy error page, most likely. Surface it as a
    // structured error rather than a JSON parse exception from deep in a
    // component.
    throw new ApiError(response.status, {
      code: "invalid_response",
      message: "the server returned a response that was not JSON",
    });
  }
}

/**
 * Absolute URL for a browser NAVIGATION to the API (OAuth start).
 *
 * These are not fetches: the response is a 302 to GitHub or Google, and the
 * state cookie has to be set on a top-level navigation for the redirect back
 * to carry it.
 */
export function apiNavigateURL(path: string): string {
  return `${API_BASE_URL}${path}`;
}
